import AccountTreeRounded from '@mui/icons-material/AccountTreeRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import {
  Box,
  Button,
  Card,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { EmptyState, PageHeader } from '../../components/states';
import { catalogKeys } from './model';

/** /bom — bills of materials: products made from components, one row per variant with one. */
export function BomListPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const list = useQuery({
    queryKey: catalogKeys.productList({ bom: true }),
    queryFn: () =>
      unwrap(api.GET('/products', { params: { query: { hasRecipe: 'true', pageSize: 100 } } })),
  });
  const rows = (list.data?.data ?? []).flatMap((p) =>
    p.variants.flatMap((v) => (v.recipe ? [{ product: p, variant: v, bom: v.recipe }] : [])),
  );
  const canCreate = can('products.update');
  const create = () => void navigate('/bom/new');

  return (
    <>
      <PageHeader
        title="Bills of materials"
        subtitle="Products made from components. Selling one takes its components from stock."
        actions={
          canCreate && (
            <Button variant="contained" startIcon={<AddRounded />} onClick={create}>
              New
            </Button>
          )
        }
      />
      <Card>
        {list.isLoading ? (
          <Box sx={{ p: 2.5 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={52} />
            ))}
          </Box>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<AccountTreeRounded />}
            title="No bills of materials yet"
            body="Pick a product and list the components one unit of it uses."
            action={canCreate ? { label: 'New bill of materials', onClick: create } : undefined}
          />
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>Components</TableCell>
                  <TableCell>Track stock</TableCell>
                  <TableCell>Return to inventory</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(({ product: p, variant: v, bom }) => (
                  <TableRow
                    key={v.id}
                    hover
                    onClick={() => void navigate(`/bom/${p.id}/${v.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {v.displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {v.sku} · per 1 {p.baseUnit.symbol}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                        {bom.lines.map((l) => (
                          <Chip
                            key={l.variantId}
                            size="small"
                            variant="outlined"
                            label={`${l.name} ${l.qty} ${l.unitSymbol}`}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>{p.trackInventory ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{bom.returnsToStock ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Card>
    </>
  );
}
